// Scripted stand-in for the Raano AI backend (design-system.md §9.3). It answers from the live
// catalog with simple keyword rules and returns the same typed blocks a real agent will.

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const findCategory = (text, categories) => {
    const name = (c) => c.name.toLowerCase();
    const pick = (test) => categories.find(test);
    if (/t-?shirt|\btees?\b/.test(text)) return pick(c => /t-?shirt|tee/.test(name(c)));
    if (/\bshirts?\b/.test(text)) return pick(c => /^shirts?$/.test(name(c))) || pick(c => /shirt/.test(name(c)) && !/t-?shirt/.test(name(c)));
    if (/jean|denim|trouser|chino|pant/.test(text)) return pick(c => /jean|trouser|pant/.test(name(c)));
    if (/jacket|bomber|leather/.test(text)) return pick(c => /jacket/.test(name(c)));
    if (/suit|blazer|tailor|wedding|festive|formal/.test(text)) return pick(c => /suit|blazer/.test(name(c)));
    return null;
};

const priceCap = (text) => {
    const m = text.match(/(?:under|below|less than|within|upto|up to)\s*₹?\s*(\d[\d,]*)/i);
    return m ? Number(m[1].replace(/,/g, '')) : null;
};

const chipsFor = (categories, exclude) => categories.filter(c => c._id !== exclude).slice(0, 3).map(c => `Shop ${c.name.toLowerCase()}`);

export const greeting = ({ page, product }) => product
    ? [{ type: 'user', text: `Show me ${product.name}.` }]
    : [
        { type: 'text', markdown: "Hi! I'm **Raano AI**. Tell me what you're shopping for (an occasion, a budget, a style) and I'll find it." },
        { type: 'chips', items: ['Best sellers for men', 'New arrivals', 'Under ₹999', 'Something for a wedding'] },
    ];

// Returns an array of blocks for one user turn
export const respond = async ({ text, context, catalog }) => {
    await sleep(900 + Math.random() * 700);
    const t = text.toLowerCase().trim();
    const { products, categories } = catalog;
    const byPrice = (list) => [...list].sort((a, b) => b.price - a.price);

    if (/checkout|pay|buy now/.test(t)) {
        return [{ type: 'text', markdown: 'Taking you to checkout…' }, { type: 'action', name: 'goto', to: '/cart' }];
    }
    if (/^add to cart$|^add (it|this)/.test(t) && context.product) {
        return [{ type: 'text', markdown: `Pick a size for **${context.product.name}**:` }, { type: 'variantPicker', productId: context.product._id }];
    }
    if (/^show me /.test(t) && context.product && t.includes(context.product.name.toLowerCase().slice(0, 12))) {
        const p = context.product;
        const cat = categories.find(c => c._id === p.category);
        return [
            { type: 'text', markdown: `Here's the **${p.name}**. ${p.description} It comes in **${p.color.toLowerCase()}**, sizes **${(p.sizes || []).join(', ')}**. Want to see what goes with it?` },
            { type: 'products', items: [p._id] },
            { type: 'chips', items: ['Add to cart', cat ? `Shop ${cat.name.toLowerCase()}` : 'New arrivals', 'What goes with this?'] },
        ];
    }
    if (/goes with|pair|match|style/.test(t) && context.product) {
        const others = products.filter(p => p.category !== context.product.category);
        return [
            { type: 'text', markdown: `These pair well with the **${context.product.name}**:` },
            { type: 'carousel', title: 'Complete the look', items: others.slice(-6).map(p => p._id) },
            { type: 'chips', items: ['Checkout', 'Under ₹999'] },
        ];
    }

    const cap = priceCap(t) || (/budget|cheap|affordable/.test(t) ? 999 : null);
    const cat = findCategory(t, categories);
    let list = products;
    if (cat) list = list.filter(p => p.category === cat._id);
    if (cap) list = list.filter(p => p.price <= cap);

    if (/best ?sell|popular|trending|top/.test(t)) {
        const picks = byPrice(list).slice(0, 3);
        return [
            { type: 'text', markdown: `Here are our **best sellers**${cat ? ` in **${cat.name}**` : ''}, the pieces customers come back for:` },
            ...picks.map(p => ({ type: 'products', items: [p._id] })),
            { type: 'showMore', label: `Show more ${cat ? cat.name : 'best sellers'}`, state: cat ? { categoryId: cat._id } : {} },
            { type: 'chips', items: chipsFor(categories, cat?._id) },
        ];
    }
    if (/new|latest|arriv/.test(t)) {
        return [
            { type: 'text', markdown: "Fresh in this week:" },
            { type: 'carousel', title: 'New arrivals', items: [...list].reverse().slice(0, 8).map(p => p._id) },
            { type: 'chips', items: chipsFor(categories) },
        ];
    }
    if (cat || cap) {
        if (!list.length) return [{ type: 'text', markdown: `I couldn't find anything${cat ? ` in **${cat.name}**` : ''}${cap ? ` under **₹${cap}**` : ''}. Try a higher budget?` }, { type: 'chips', items: ['Best sellers for men', 'New arrivals'] }];
        return [
            { type: 'text', markdown: `I found **${list.length}** ${list.length === 1 ? 'piece' : 'pieces'}${cat ? ` in **${cat.name}**` : ''}${cap ? ` under **₹${cap}**` : ''}:` },
            { type: 'carousel', title: cat ? cat.name : `Under ₹${cap}`, items: list.slice(0, 8).map(p => p._id) },
            { type: 'chips', items: chipsFor(categories, cat?._id) },
        ];
    }
    return [
        { type: 'text', markdown: "I can help you find clothes by **occasion**, **budget** or **category**. For example, *“shirts under ₹1,500”* or *“something for a wedding”*." },
        { type: 'chips', items: ['Best sellers for men', 'New arrivals', 'Under ₹999'] },
    ];
};
